'use strict';
import React, { useEffect, useState } from 'react';
import { BrowserRouter, Route, Switch, useHistory, useLocation } from 'react-router-dom';
import { useDispatch, useSelector } from 'react-redux';
import './theme/theme.css';
import('./theme/css/material-icons.css');
import { MainLayout } from './views/layouts/mainLayout/MainLayout.jsx';
import UtilPage from './views/util/UtilContainer.jsx';
import Login from './views/login/Login.jsx';
import Faq from './views/faq/faq.jsx';
import TermsContainer from './views/terms/TermsOfServiceContainer.jsx';
import LandingContainer from './views/landing/LandingContainer.jsx';
import NotFound from './views/NotFound.jsx';
import { ErrorBoundary } from './components/index.jsx';
import ScrollCalculator from './components/scroll/ScrollCalculator.jsx';
import RouteAuthorization from './security/RouteAuthorization.jsx';
import VerifyEmail from './views/VerifyEmail.jsx';
import StatsFetcher from './views/public/StatsFetcher.jsx';
import DiscoveryPanels from './views/discovery-panels/DiscoveryPanels.jsx';
import * as config from './config/env.config.js';
import * as permissions from './security/permissionTypes.js';
import urljoin from 'url-join';
import { Resources } from './views/home/resources/Resources.jsx';
import { useAuth } from './hooks/auth.hook';
import Callback from './callback/Callback.jsx';
import SecurityCheck from './views/util/SecurityCheck.jsx';
import { Logger, Log } from './utils/Logger.js';
import { AppInsightLogLevel } from './enums/Enums.js';
const logger = new Logger();
import './App.scss';
import initializeAnalytics from './utils/AnalyticsTools';
import { useCurrentUser } from '@/redux/services/portal/user.api.js';
import { useAuth0 } from '@auth0/auth0-react';
import { EulaConfirmationModal } from './components/modals/EulaConfirmationModal.jsx';
import { useAcceptEulaMutation } from '@/redux/services/portal/user.api.js';
import { Box, Typography, CircularProgress } from '@mui/material';
import Modal from 'metabux/components/Modal/Modal';
import { LoadingProvider } from './contexts/LoadingContext';
import { GlobalLoading } from './components/GlobalLoading';
import AssistantWidget from "./widgets/AssistantWidget.jsx";
import { useIsAuthenticated } from './hooks/useIsAuthenticated';
import { setUserProfile } from './redux/reducers/userProfile.reducer';
import StorageBlockedWarning from './components/StorageBlockedWarning';
import { setBinfAuthCookie } from './utils/auth0Token';

initializeAnalytics(config.DATADOG_TOKEN, config.DATADOG_URL, config.ENV, history);

// --- Assistant token fetcher (BFF endpoint) ---
async function getAssistantToken() {
  const endpoint = urljoin(config.API_BASEPATH_PORTAL || "http://localhost:3005/api/v1", "assistant/token/portal");
  const res = await fetch(endpoint, {
    method: "POST",
    credentials: "include"
  });
  if (!res.ok) {
    const body = await res.text().catch(() => "");
    console.error("Assistant token fetch failed:", res.status, body);
    throw new Error("Failed to fetch assistant token");
  }
  const data = await res.json();
  // The API returns { Token, ExpiresIn, Tier }, extract Token
  return data.Token || data.token;
}


/* istanbul ignore next */
// Inner component that has access to BrowserRouter context
const AppContent = ({ dispatch, auth, showEulaModal, setShowEulaModal, showLogoutLoading, setShowLogoutLoading, user, userIsLoading, error, acceptEula, isAuthenticated, auth0IsLoading, props }) => {
	const history = useHistory();

	// Handle redirects for unauthenticated users trying to access protected routes
	// Note: We let RouteAuthorization handle redirects to login for protected routes
	// This prevents redirect loops after logout
	const location = useLocation();
	
	// Show loading modal when logout is in progress - let Auth0 handle the redirect
	useEffect(() => {
		const checkLogout = () => {
			const logoutInProgress = localStorage.getItem('logout_in_progress');
			
			if (logoutInProgress === 'true') {
				// If we're on the landing page, Auth0 has completed the redirect
				// Clear flag immediately to prevent flicker
				if (location.pathname === '/') {
					localStorage.removeItem('logout_in_progress');
					setShowLogoutLoading(false);
				} else {
					// Still waiting for Auth0 redirect - show loading modal
					setShowLogoutLoading(true);
				}
			} else {
				setShowLogoutLoading(false);
			}
		};
		
		// Listen for custom event when logout is initiated
		const handleLogoutInitiated = () => {
			checkLogout();
		};
		
		window.addEventListener('logout-initiated', handleLogoutInitiated);
		
		// Check immediately on mount and when pathname changes
		checkLogout();
		
		// Also check periodically to catch when flag is set (before pathname changes)
		// This ensures the modal shows immediately when logout is initiated
		// Reduced frequency to 200ms to improve performance
		const interval = setInterval(checkLogout, 200);
		
		return () => {
			window.removeEventListener('logout-initiated', handleLogoutInitiated);
			clearInterval(interval);
		};
	}, [location.pathname, setShowLogoutLoading]); // Re-check when pathname changes (after Auth0 redirect)
	
	useEffect(() => {
		const logoutInProgress = localStorage.getItem('logout_in_progress');
		
		// Don't do any redirects during logout - let Auth0 handle the redirect
		// Also prevent silent auth from running during logout
		if (logoutInProgress === 'true') {
			return;
		}
		
		const currentPath = location.pathname;
		const protectedPaths = ['/home', '/version', '/errortest', '/discovery-panels'];
		const isProtectedPath = protectedPaths.some(path => currentPath.startsWith(path));
		
		// Only redirect to landing page (not login) if:
		// 1. We're on a protected path
		// 2. No valid token exists AND no user data
		// 3. User data has finished loading (or failed to load)
		// 4. We're not already on login or landing page
		// 5. Auth0 is not still loading (to avoid redirecting during back navigation)
		// Note: RouteAuthorization will handle redirects to login for protected routes when user tries to access them
		// This just handles the case where user lands on a protected path without being authenticated
		// isAuthenticated is passed from parent App component
		if (isProtectedPath && !isAuthenticated && !user && !userIsLoading && !auth0IsLoading) {
			if (currentPath !== '/login' && currentPath !== '/') {
				// Redirect to landing page, not login - user can click sign in button if they want to login
				history.replace('/');
			}
		}
	}, [isAuthenticated, user, userIsLoading, history, location.pathname, auth0IsLoading]);

	const handleEulaAccept = async () => {
		try {
			await acceptEula();
			setShowEulaModal(false);
			// User data will be refetched automatically due to invalidatesTags
		} catch (error) {
			console.error('Failed to accept EULA:', error);
			// Show error to user and allow retry
			alert('Failed to accept EULA. Please try again. If the problem persists, please contact support.');
			// Don't close modal on error - user can try again
		}
	};

	const handleEulaDecline = () => {
		// If user declines, log them out and redirect to landing page
		// Note: / must be whitelisted in Auth0 Allowed Logout URLs
		auth.logout('/');
		setShowEulaModal(false);
	};

	return (
		<ErrorBoundary history={props.history}>
			<ScrollCalculator />
			<EulaConfirmationModal 
				open={showEulaModal}
				onAccept={handleEulaAccept}
				onDecline={handleEulaDecline}
			/>
			<Modal
				open={showLogoutLoading}
				onClose={() => {}} // Prevent closing during logout
				hideCloseButton={true}
			>
				<Box
					sx={{
						display: 'flex',
						flexDirection: 'column',
						alignItems: 'center',
						justifyContent: 'center',
						gap: 2,
						padding: 4
					}}
				>
					<CircularProgress size={40} />
					<Typography variant="body1">Signing out...</Typography>
				</Box>
			</Modal>
			<Root>
				<Switch>
					<Route path={"/"} exact render={({ match }) => <LandingContainer match={match} auth={auth} {...props} />} />
					<Route path={"/home"} match={props.match} component={AuthMain} />
					<Route path={"/version"} match={props.match} component={AuthMain} />
					<Route path={"/errortest"} match={props.match} component={AuthMain} />
					<Route path={"/discovery-panels/:projectId/:productType/:sampleSetId"} match={props.match} component={AuthSP} />
					<Route path={"/login"} render={(routeProps) => <Login {...routeProps} />} />
					<Route path={"/callback"} render={(routeProps) => <Callback currentUser={user} requestFetch={error} {...routeProps} />} />
					<Route path={"/securitycheck"} component={SecurityCheck} />
					<Route path={"/terms"} exact render={({ match }) => <TermsContainer match={match} auth={auth} {...props} />} />
					<Route path={"/help"} exact render={({ match }) => <Faq match={match} auth={auth} {...props} />} />
					<Route path={"/verifyemail"} exact render={(routeProps) => <VerifyEmail {...routeProps} auth={auth} {...props} />} />
					<Route path={"/resources"} exact render={() => <Resources {...props} />} />
					<Route path={"/stats/table"} exact render={({ match }) => <StatsFetcher match={match} auth={auth} {...props} />} />
					<Route path={"/utils"} exact render={({ match }) => <UtilPage match={match} auth={auth} {...props} />} />
					<Route render={({ match }) => <NotFound auth={auth} {...props} />} />
				</Switch>
			</Root>
		</ErrorBoundary>
	);
};

const App = (props) => {
	const dispatch = useDispatch();
	const auth = useAuth();
	const isAuthenticated = useIsAuthenticated();
	const { isLoading: auth0IsLoading, user: auth0User, getAccessTokenSilently } = useAuth0();
	const [showEulaModal, setShowEulaModal] = useState(false);
	const [showLogoutLoading, setShowLogoutLoading] = useState(false);

	const { data: user, isLoading: userIsLoading, error } = useCurrentUser();
	const [acceptEula] = useAcceptEulaMutation();


	// Set and refresh binf_auth_cookie when user authenticates
	// Cookie is refreshed periodically to ensure it stays valid
	useEffect(() => {
		if (!isAuthenticated || auth0IsLoading || !getAccessTokenSilently) {
			return;
		}
		
		let isMounted = true;
		let refreshInterval = null;
		
		const refreshCookie = async () => {
			if (!isMounted) return;
			
			try {
				const token = await getAccessTokenSilently({
					audience: 'https://portal-api'
				});
				if (isMounted && token) {
					setBinfAuthCookie(token);
				}
			} catch (error) {
				if (error.error === 'login_required' || error.message?.includes('login_required')) {
					return;
				}
				console.warn('Could not refresh binf_auth_cookie:', error);
			}
		};
		
		// Set cookie immediately on authentication
		refreshCookie();
		
		// Refresh cookie every hour to keep it valid
		refreshInterval = setInterval(refreshCookie, 60 * 60 * 1000);
		
		return () => {
			isMounted = false;
			if (refreshInterval) {
				clearInterval(refreshInterval);
			}
		};
	}, [isAuthenticated, auth0IsLoading, getAccessTokenSilently]);
	
	// Check if EULA needs to be shown when user is authenticated
	useEffect(() => {
		// Don't show EULA modal if feature flag is disabled
		if (!config.ENABLE_EULA_MODAL) {
			setShowEulaModal(false);
			return;
		}
		
		if (isAuthenticated && user && !userIsLoading) {
			// Only show EULA modal if user hasn't accepted it yet
			if (!user.eulaAccepted) {
				setShowEulaModal(true);
			} else {
				setShowEulaModal(false);
			}
		} else {
			// Hide EULA modal if user is not logged in or data is loading
			setShowEulaModal(false);
		}
	}, [isAuthenticated, user, userIsLoading]);
	
	// Authentication state is now managed by Auth0 SDK and synced to Redux in auth.hook.js
	// No manual token validation needed - SDK handles it automatically


	// Listen for Auth0 SDK cache changes from other tabs (cross-tab logout detection)
	useEffect(() => {
		const handleStorageChange = (e) => {
			// Check if Auth0 SDK cache was cleared (logout in another tab)
			// Auth0 SDK stores cache with keys like @@auth0spajs@@::...
			if (e.key && e.key.includes('@@auth0spajs@@') && e.newValue === null) {
				// Auth0 cache was cleared in another tab - user logged out
				console.log('🔄 Cross-tab logout detected - Auth0 cache cleared');
				auth.clearStorage();
				// Redirect to login if not already there
				if (window.location.pathname !== '/login' && window.location.pathname !== '/') {
					window.location.href = '/login';
				}
			}
		};

		window.addEventListener('storage', handleStorageChange);
		return () => {
			window.removeEventListener('storage', handleStorageChange);
		};
	}, [auth]);

	const logViewLoad = (viewName) => {
		let logFilterChange = new Log();
		logFilterChange.SetLevel(AppInsightLogLevel.EVENT);
		logFilterChange.SetName('PageView_' + viewName);
		logFilterChange.AddProperty('view', viewName);
		logger.doLog(logFilterChange);
	};

	useEffect(() => {
		const unlisten = props.history?.listen((location, action) => {
			if (location.pathname.toLowerCase().indexOf("statstable") != -1) {
				logViewLoad("StatsTable");
			} else if (location.pathname.toLowerCase().indexOf("pathwayexplorer") != -1) {
				logViewLoad("PathwayExplorer");
			} else if (location.pathname.toLowerCase().indexOf("home/project") != -1) {
				logViewLoad("Results");
			}
			else if (location.pathname.toLowerCase().lastIndexOf("home") != -1) {
				logViewLoad("ProjectsHome");
			}
		});
		return unlisten;
	}, [props.history]);

	const SUPPORT_AGENT_API = config.SUPPORT_AGENT_API || "https://localhost:3010";
	const AGENT_WIDGET_CDN_URL = config.AGENT_WIDGET_URL || "http://localhost:5173";

	if (userIsLoading) return null;

	return (
		<LoadingProvider>
			<BrowserRouter>
				<GlobalLoading showEulaModal={showEulaModal} />
				<StorageBlockedWarning />
				<AppContent 
					dispatch={dispatch}
					auth={auth}
					showEulaModal={showEulaModal}
					setShowEulaModal={setShowEulaModal}
					showLogoutLoading={showLogoutLoading}
					setShowLogoutLoading={setShowLogoutLoading}
					user={user}
					userIsLoading={userIsLoading}
					error={error}
					acceptEula={acceptEula}
					isAuthenticated={isAuthenticated}
					auth0IsLoading={auth0IsLoading}
					props={props}
				/>
				{config.ENABLE_ASSISTANT_WIDGET && (
					<AssistantWidget
						apiRag={`${SUPPORT_AGENT_API}/api/v1/rag`}
						apiInfo={`${SUPPORT_AGENT_API}/api/v1/info`}
						apiMemory={`${SUPPORT_AGENT_API}/api/v1/memory`}
						getAssistantToken={getAssistantToken}
						isAuthenticated={isAuthenticated}
						cdnUrl={`${AGENT_WIDGET_CDN_URL}/agent-widget.js`}
						config={{
							welcomeMessage: "Hi! I'm your AI Support Assistant. How can I help you today?",
							widget: {
								width: 360,
								height: 560,
								borderRadius: 12,
								shadow: "0 8px 30px rgba(0,0,0,.15)"
							}
						}}
					/>
				)}
			</BrowserRouter>
		</LoadingProvider>
	);
	 
};

const AuthMain = (props) => {
	const allowedPermissions = permissions.CAN_VIEW_APERTURE;
	return (
		<RouteAuthorization
			allowedPermissions={allowedPermissions}
			WrappedComponent={MainLayout}
			{...props}
		/>
	);
};

const AuthSP = (props) => {
	const allowedPermissions = permissions.CAN_VIEW_APERTURE;
	return (
		<RouteAuthorization
			allowedPermissions={allowedPermissions}
			WrappedComponent={DiscoveryPanels}
			{...props}
		/>
	);
};



const Root = (props) => (
	<div className="app" data-testid="app" {...props} />
);

export default App;
